// frontend/src/lib/pdfUtils.ts
import { jsPDF } from 'jspdf'

export interface PdfCompanyInfo {
  legal_name?: string | null
  tax_id?: string | null
  business_activity?: string | null
  address_street?: string | null
  address_city?: string | null
  logo_url?: string | null
}

const BLUE: [number, number, number] = [58, 98, 216]

/** Fetches a logo URL and converts it to a base64 data URL for jsPDF. */
export async function loadLogoBase64(url: string): Promise<string | null> {
  try {
    const res = await fetch(url)
    const blob = await res.blob()
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = () => resolve(null)
      reader.readAsDataURL(blob)
    })
  } catch {
    return null
  }
}

/**
 * Renders the professional company header on the current jsPDF page.
 * Returns the Y coordinate where content should start after the header.
 */
export function addPdfHeader(
  doc: jsPDF,
  company: PdfCompanyInfo,
  title: string,
  period: string,
  logoBase64?: string | null,
): number {
  const pageWidth = doc.internal.pageSize.getWidth()
  let y = 12

  // Logo — top right
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, 'AUTO', pageWidth - 46, y - 4, 36, 18)
    } catch {
      // silently skip broken logo
    }
  }

  // Company name — top left, bold
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(30, 40, 60)
  doc.text(company.legal_name || '', 14, y)
  y += 5

  // Company details — smaller
  doc.setFontSize(8)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(80, 80, 80)
  if (company.tax_id) { doc.text(`RUT: ${company.tax_id}`, 14, y); y += 4 }
  if (company.business_activity) { doc.text(company.business_activity, 14, y); y += 4 }
  if (company.address_street) {
    const addr = company.address_city
      ? `${company.address_street}, ${company.address_city}`
      : company.address_street
    doc.text(addr, 14, y)
    y += 4
  }

  // Blue separator line
  y += 2
  doc.setDrawColor(...BLUE)
  doc.setLineWidth(0.5)
  doc.line(14, y, pageWidth - 14, y)
  y += 6

  // Report title — centered
  doc.setFontSize(13)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(30, 40, 60)
  doc.text(title, pageWidth / 2, y, { align: 'center' })
  y += 5

  // Period — centered, lighter
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(100, 100, 100)
  doc.text(period, pageWidth / 2, y, { align: 'center' })
  y += 7

  // Reset text color to default
  doc.setTextColor(30, 40, 60)

  return y
}

/**
 * Adds signature spaces at the bottom of the last page.
 * Call once after all content is rendered.
 */
export function addPdfFooter(doc: jsPDF): void {
  const pageHeight = doc.internal.pageSize.getHeight()
  const pageWidth = doc.internal.pageSize.getWidth()
  const y = pageHeight - 25

  doc.setDrawColor(180, 180, 180)
  doc.setLineWidth(0.3)

  const col1X = 45
  doc.line(col1X - 28, y, col1X + 28, y)
  doc.setFontSize(7)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(100, 100, 100)
  doc.text('Firma Contador', col1X, y + 4, { align: 'center' })

  const col2X = pageWidth - 45
  doc.line(col2X - 28, y, col2X + 28, y)
  doc.text('Firma Representante Legal', col2X, y + 4, { align: 'center' })
}
